import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Download, BookOpen, FlaskConical, Calendar,
  FileText, ScrollText, Sigma, ListChecks, BookMarked,
  Atom, ClipboardList, Newspaper, Search, FileDown, MessageCircle
} from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { INSTITUTE } from "@/lib/content";
import LeadCaptureDialog from "@/components/LeadCaptureDialog";

// SEO file-naming convention: kothari-classes-jalgaon-[subject]-[type].pdf
const fname = (subject, type) =>
  `kothari-classes-jalgaon-${subject}-${type}.pdf`.toLowerCase().replace(/\s+/g, "-");

const CATEGORIES = [
  {
    key: "boards",
    title: "Board Excellence Vault",
    accent: "SSC · CBSE · State Board",
    icon: BookOpen,
    items: [
      { name: "SSC PYQs", desc: "Previous-year question banks for Maharashtra SSC.", icon: FileText, filename: fname("ssc", "pyqs") },
      { name: "CBSE Papers", desc: "Last 10 years CBSE 10th & 12th solved papers.", icon: FileText, filename: fname("cbse", "papers") },
      { name: "Topper Sheets", desc: "Annotated answer sheets from Kothari toppers.", icon: ScrollText, filename: fname("boards", "topper-sheets") },
      { name: "Maths Formula Booklet", desc: "Class-wise formula sheets, ready to revise.", icon: Sigma, filename: fname("maths", "formula-booklet") },
      { name: "Science Laws Quick-Sheet", desc: "Physics & Chemistry laws + applications.", icon: FlaskConical, filename: fname("science", "laws-quick-sheet") },
      { name: "Maharashtra Board Syllabus 2026", desc: "Updated SSC & HSC syllabus reference.", icon: ListChecks, filename: fname("maharashtra-board", "syllabus-2026") },
    ],
  },
  {
    key: "competitive",
    title: "Competitive Edge Vault",
    accent: "JEE · NEET · MHT-CET",
    icon: Atom,
    items: [
      { name: "JEE Weightage Map", desc: "Topic-wise weightage analysis for JEE Mains.", icon: ListChecks, filename: fname("jee", "weightage-map") },
      { name: "NEET High-Yield Topics", desc: "Most-asked NEET topics across last 10 years.", icon: BookMarked, filename: fname("neet", "high-yield-topics") },
      { name: "MHT-CET Papers", desc: "Solved MHT-CET papers with shortcuts.", icon: FileText, filename: fname("mht-cet", "papers") },
      { name: "NEET Model Test", desc: "Full-length NEET simulation with answer key.", icon: ClipboardList, filename: fname("neet", "model-test") },
      { name: "Organic Chemistry Charts", desc: "Reaction mechanisms & named reactions.", icon: FlaskConical, filename: fname("chemistry", "organic-charts") },
      { name: "Physics Mind Maps", desc: "Visual concept maps for fast revision.", icon: Atom, filename: fname("physics", "mind-maps") },
    ],
  },
  {
    key: "schedule",
    title: "Schedule & Updates",
    accent: "Calendars · Brochures · K-SAT",
    icon: Calendar,
    items: [
      { name: "Academic Calendar", desc: "Full-year academic plan and milestones.", icon: Calendar, filename: fname("academic", "calendar") },
      { name: "Test Series Schedule", desc: "Dates for Kothari's 500+ Test Series.", icon: ClipboardList, filename: fname("test-series", "schedule") },
      { name: "Holiday Calendar", desc: "Branch holiday schedule for the year.", icon: Calendar, filename: fname("holiday", "calendar") },
      { name: "K-SAT Paper", desc: "Kothari Scholastic Aptitude Test sample.", icon: FileText, filename: fname("k-sat", "paper") },
      { name: "Brochure", desc: "Official Kothari Classes brochure.", icon: FileDown, filename: fname("institute", "brochure") },
      { name: "Newsletter", desc: "Latest issue of the Kothari Newsletter.", icon: Newspaper, filename: fname("kothari", "newsletter") },
    ],
  },
];

export default function Resources() {
  useSEO({
    title: "Student Resource Center | Kothari Classes Jalgaon",
    description: "Free study resources from Kothari Classes — SSC PYQs, CBSE Papers, Topper Sheets, JEE/NEET/MHT-CET material, formula booklets and more. Your digital library for Board Excellence and Competitive Success.",
    canonical: "https://www.kothariclasses.in/resources",
  });

  const [q, setQ] = useState("");
  const [activeResource, setActiveResource] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!q) return CATEGORIES;
    const t = q.toLowerCase();
    return CATEGORIES.map((c) => ({
      ...c,
      items: c.items.filter((i) => (i.name + i.desc).toLowerCase().includes(t)),
    })).filter((c) => c.items.length > 0);
  }, [q]);

  const requestUrl = `https://wa.me/${INSTITUTE.whatsapp_number}?text=${encodeURIComponent("Hi, I'd like to access the Kothari Classes Resource Center materials.")}`;

  const openLead = (it) => {
    setActiveResource(it);
    setDialogOpen(true);
  };

  return (
    <div data-testid="resources-page">
      {/* Header */}
      <section className="bg-gray-950 text-white py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.04]" style={{backgroundImage:"radial-gradient(circle at 2px 2px, #fff 1px, transparent 1px)", backgroundSize:"32px 32px"}}/>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Free for every student</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]">
              Student Resource Center
            </h1>
            <p className="mt-5 text-gray-400 text-lg max-w-xl">
              Your digital library for <span className="text-white font-semibold">Board Excellence</span> and <span className="text-white font-semibold">Competitive Success</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Search bar */}
      <section className="bg-white border-b border-gray-200 sticky top-20 lg:top-24 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
            {filtered.reduce((n, c) => n + c.items.length, 0)} resources available
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search resources..."
              data-testid="resources-search"
              className="w-full border border-gray-300 pl-9 pr-4 py-3 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      {filtered.length === 0 && (
        <section className="bg-gray-50 py-20 text-center text-gray-500" data-testid="resources-empty">
          No resources match your search.
        </section>
      )}

      {filtered.map((cat, ci) => (
        <section key={cat.key} className={`${ci % 2 === 0 ? "bg-gray-50" : "bg-white"} py-14 lg:py-20`} data-testid={`resources-category-${cat.key}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4 pb-4 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <span className="font-display text-xs font-black text-red-600 tracking-[0.3em]">0{ci + 1}</span>
                <div>
                  <h2 className="font-display text-2xl lg:text-3xl font-black text-gray-950">{cat.title}</h2>
                  <span className="text-xs uppercase tracking-widest text-gray-500">{cat.accent}</span>
                </div>
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500 inline-flex items-center gap-2">
                <cat.icon className="w-4 h-4 text-red-600" /> {cat.items.length} resources
              </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.items.map((it, i) => (
                <button
                  key={it.name}
                  type="button"
                  onClick={() => openLead(it)}
                  data-testid={`resource-${cat.key}-${i}`}
                  className="group bg-white border border-gray-200 p-6 card-lift block text-left w-full"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 border border-red-200 bg-red-50 flex items-center justify-center text-red-600">
                      <it.icon className="w-5 h-5" />
                    </div>
                    <Download className="w-4 h-4 text-gray-300 group-hover:text-red-600 transition-colors" />
                  </div>
                  <h3 className="font-display text-base font-bold text-gray-950 mt-5 leading-tight group-hover:text-red-600 transition-colors">
                    {it.name}
                  </h3>
                  <p className="text-xs text-gray-600 mt-2 leading-relaxed">{it.desc}</p>
                  <div className="text-[10px] font-mono text-gray-400 mt-3 truncate">{it.filename}</div>
                  <div className="text-[10px] font-bold tracking-widest uppercase text-red-600 mt-4 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Download <ArrowRight className="w-3 h-3" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="bg-red-600 text-white py-14" data-testid="resources-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-5">
          <div>
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-white/80">Need a specific resource?</span>
            <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight mt-2">Ask our faculty directly.</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={requestUrl} target="_blank" rel="noopener noreferrer" className="bg-white text-red-600 font-semibold px-6 py-3.5 hover:bg-gray-100 transition-colors inline-flex items-center gap-2" data-testid="resources-whatsapp">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
            <Link to="/admissions" className="border border-white text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-red-600 transition-colors inline-flex items-center gap-2">
              Book Free Demo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <LeadCaptureDialog
        resource={activeResource}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
